import React, { useState, useMemo } from 'react';
import {
  Table,
  Input,
  Space,
  Typography,
  Button,
  Modal,
  Form,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getMarketIndicators, updateMarketIndicatorDescription } from '../../api/project';

const { Text } = Typography;

function MarketLabelsTab() {
  const queryClient = useQueryClient();
  const [labelSearch, setLabelSearch] = useState('');
  const [editingIndicator, setEditingIndicator] = useState<any>(null);
  const [editForm] = Form.useForm();

  const {
    data: marketIndicatorsData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['adminMarketIndicators'],
    queryFn: getMarketIndicators,
  });

  const { mutate: updateDescription, isLoading: isUpdating } = useMutation(
    ({ id, description }: { id: string; description: string }) =>
      updateMarketIndicatorDescription(id, description),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['adminMarketIndicators']);
        setEditingIndicator(null);
        editForm.resetFields();
      },
      onError: () => {},
    },
  );

  const rows: any[] = useMemo(() => {
    const raw = Array.isArray(marketIndicatorsData)
      ? marketIndicatorsData
      : marketIndicatorsData?.response || [];
    return raw;
  }, [marketIndicatorsData]);

  const filteredRows = useMemo(() => {
    const text = labelSearch.toLowerCase().trim();
    if (!text) return rows;
    return rows.filter((row: any) =>
      [row.display_name, row.short_display_name, row.name, row.category?.display_name, row.description]
        .join(' ')
        .toLowerCase()
        .includes(text),
    );
  }, [rows, labelSearch]);

  const columns: any[] = [
    {
      title: 'Category',
      key: 'category',
      width: 140,
      render: (_: any, record: any) => record.category?.display_name || '—',
      sorter: (a: any, b: any) =>
        (a.category?.display_name || '').localeCompare(b.category?.display_name || ''),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: (
        <span>
          Display Name&nbsp;
          <Tooltip title="This is the full label shown on the executive summary page and in filter menus.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'default' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'display_name',
      key: 'display_name',
      width: 180,
      sorter: (a: any, b: any) =>
        (a.display_name || '').localeCompare(b.display_name || ''),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: (
        <span>
          Short Name&nbsp;
          <Tooltip title="A shorter version of the label used in compact chips on the search results list.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'default' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'short_display_name',
      key: 'short_display_name',
      width: 160,
      render: (v: string | null) => v || '—',
      sorter: (a: any, b: any) =>
        (a.short_display_name || '').localeCompare(b.short_display_name || ''),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Icon',
      dataIndex: 'icon_name',
      key: 'icon_name',
      width: 80,
      render: (v: string | null, record: any) => {
        const color = record.color_hex || '#8c8c8c';
        const style = { fontSize: 16, color };
        if (v === 'check') return <CheckCircleOutlined style={style} />;
        if (v === 'question') return <QuestionCircleOutlined style={style} />;
        if (v === 'clock') return <ClockCircleOutlined style={style} />;
        if (v === 'x') return <CloseCircleOutlined style={style} />;
        if (v === 'dash' || v === '-') return <MinusCircleOutlined style={style} />;
        return '—';
      },
    },
    {
      title: 'Color',
      dataIndex: 'color_hex',
      key: 'color_hex',
      width: 100,
      render: (v: string | null) =>
        v ? (
          <Space>
            <span
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                borderRadius: 3,
                background: v,
                border: '1px solid #d9d9d9',
                verticalAlign: 'middle',
              }}
            />
            <Text style={{ fontSize: 12 }}>{v}</Text>
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (a: any, b: any) =>
        (a.description || '').localeCompare(b.description || ''),
      sortDirections: ['ascend', 'descend'] as const,
      render: (v: string) => (
        <Tooltip title={v} overlayStyle={{ maxWidth: 420 }}>
          <Text
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              cursor: 'default',
            }}
          >
            {v || '—'}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: any, record: any) => (
        <Button
          icon={<EditOutlined />}
          size="small"
          onClick={() => {
            setEditingIndicator(record);
            editForm.setFieldsValue({ description: record.description });
          }}
        />
      ),
    },
  ];

  return (
    <>
      <div style={{ margin: '16px 0' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name, category, description…"
          value={labelSearch}
          onChange={(e) => setLabelSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        showSorterTooltip={false}
        loading={isLoading || isFetching}
        pagination={{ pageSize: 50 }}
      />

      <Modal
        title={
          editingIndicator
            ? `Edit description — ${editingIndicator.category?.display_name} ${editingIndicator.display_name}`
            : 'Edit description'
        }
        open={!!editingIndicator}
        onCancel={() => {
          setEditingIndicator(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        okText="Save"
        confirmLoading={isUpdating}
        width={560}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            if (!editingIndicator) return;
            updateDescription({ id: editingIndicator.id, description: values.description });
          }}
        >
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <Input.TextArea rows={5} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default MarketLabelsTab;
